/**
 * Generic MCP OAuth Authentication
 * Handles OAuth2 authentication flow for generic remote MCP servers
 * Supports multiple servers with different OAuth configurations
 */
import config from '../config';
import { getBffHeaders, getBeHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

/**
 * Storage keys for authentication data (per server)
 */
const getStorageKeys = (serverId) => ({
  ACCESS_TOKEN: `generic_mcp_${serverId}_access_token`,
  REFRESH_TOKEN: `generic_mcp_${serverId}_refresh_token`,
  EXPIRES_AT: `generic_mcp_${serverId}_expires_at`,
  USER_INFO: `generic_mcp_${serverId}_user_info`,
  SERVER_NAME: `generic_mcp_${serverId}_server_name`,
});

/**
 * Generic MCP Authentication Manager
 */
class GenericMcpAuth {
  constructor() {
    this.authStatuses = new Map(); // Map of serverId -> auth status
  }

  /**
   * Start OAuth flow for a specific server
   * Opens authorization URL in a popup window
   */
  async startAuthFlow(serverId) {
    try {
      // Get OAuth configuration from BFF
      const response = await getAuthorizedFetch()(`${config.bffUrl}/bff/auth/generic-mcp/${serverId}/login`, {
        method: 'GET',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to initiate OAuth: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.authUrl) {
        throw new Error('No authorization URL received from server');
      }

      const {authUrl, serverName} = result;
      
      // Store server name for later use
      if (serverName) {
        localStorage.setItem(getStorageKeys(serverId).SERVER_NAME, serverName);
      }

      // Open OAuth URL in popup window
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        authUrl,
        `${serverId}_oauth`,
        `width=${width},height=${height},left=${left},top=${top},popup=1`
      );

      // Listen for OAuth callback message
      return new Promise((resolve, reject) => {
        const messageHandler = async (event) => {
          // Verify origin for security
          if (event.origin !== window.location.origin) {
            return;
          }

          if (event.data.type === `generic-mcp-${serverId}-auth-success`) {
            window.removeEventListener('message', messageHandler);
            
            // Store tokens
            const {code, state} = event.data;
            
            try {
              await this.exchangeCodeForTokens(serverId, code, state);
              resolve(true);
            } catch (error) {
              reject(error);
            }
          } else if (event.data.type === `generic-mcp-${serverId}-auth-error`) {
            window.removeEventListener('message', messageHandler);
            reject(new Error(event.data.error || 'Authentication failed'));
          }
        };

        window.addEventListener('message', messageHandler);

        // Check if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          window.removeEventListener('message', messageHandler);
          reject(new Error('Popup was blocked. Please allow popups for this site.'));
        }

        // Timeout after 5 minutes
        setTimeout(() => {
          window.removeEventListener('message', messageHandler);
          reject(new Error('Authentication timeout'));
        }, 5 * 60 * 1000);
      });

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Generic MCP (${serverId}) OAuth error:`, error);
      throw error;
    }
  }

  /**
   * Exchange authorization code for tokens
   */
  async exchangeCodeForTokens(serverId, code, state, codeVerifier = null) {
    try {
      const response = await getAuthorizedFetch()(`${config.bffUrl}/bff/auth/generic-mcp/${serverId}/token`, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          code,
          state,
          code_verifier: codeVerifier,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Token exchange failed: ${response.status}`);
      }

      const tokens = await response.json();
      
      // Store tokens
      const storageKeys = getStorageKeys(serverId);
      localStorage.setItem(storageKeys.ACCESS_TOKEN, tokens.access_token);
      
      if (tokens.refresh_token) {
        localStorage.setItem(storageKeys.REFRESH_TOKEN, tokens.refresh_token);
      }
      
      // Calculate and store expiration time
      if (tokens.expires_in) {
        const expiresAt = Date.now() + (tokens.expires_in * 1000);
        localStorage.setItem(storageKeys.EXPIRES_AT, expiresAt.toString());
      }
      
      if (tokens.serverName) {
        localStorage.setItem(storageKeys.SERVER_NAME, tokens.serverName);
      }

      // Fetch and store user info if available
      try {
        const userInfo = await this.fetchUserInfo(serverId);
        if (userInfo) {
          localStorage.setItem(storageKeys.USER_INFO, JSON.stringify(userInfo));
        }
      } catch (error) {
        // User info fetch is optional
        // eslint-disable-next-line no-console
        console.debug(`Could not fetch user info for ${serverId}:`, error);
      }

      // Notify backend about new credentials
      await this.notifyBackendCredentials(serverId, tokens.access_token, tokens.refresh_token);

      return tokens;

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Generic MCP (${serverId}) token exchange error:`, error);
      throw error;
    }
  }

  /**
   * Fetch user info from the OAuth provider
   */
  async fetchUserInfo(serverId) {
    try {
      const storageKeys = getStorageKeys(serverId);
      const accessToken = localStorage.getItem(storageKeys.ACCESS_TOKEN);
      
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await getAuthorizedFetch()(`${config.bffUrl}/bff/auth/generic-mcp/${serverId}/user`, {
        method: 'GET',
        headers: getBffHeaders({
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        }),
      });

      if (!response.ok) {
        // User info endpoint might not be available for all servers
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch user info: ${response.status}`);
      }

      return await response.json();

    } catch (error) {
      // eslint-disable-next-line no-console
      console.debug(`Generic MCP (${serverId}) user info error:`, error);
      return null;
    }
  }

  /**
   * Notify backend about available credentials
   */
  async notifyBackendCredentials(serverId, accessToken, refreshToken) {
    try {
      // The backend will automatically pick up credentials from the shared credential manager
      // This is just to trigger a refresh of the MCP connection if needed
      await getAuthorizedFetch()(`${config.agentUrl}/api/mcp/generic/${serverId}/status`, {
        method: 'GET',
        headers: getBeHeaders({
          'Content-Type': 'application/json',
        }),
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.debug(`Could not notify backend about ${serverId} credentials:`, error);
    }
  }

  /**
   * Check if authenticated for a specific server
   */
  async isAuthenticated(serverId) {
    try {
      const storageKeys = getStorageKeys(serverId);
      const accessToken = localStorage.getItem(storageKeys.ACCESS_TOKEN);
      
      if (!accessToken) {
        return false;
      }

      // Check if token is expired
      const expiresAt = localStorage.getItem(storageKeys.EXPIRES_AT);
      if (expiresAt) {
        const now = Date.now();
        if (now >= parseInt(expiresAt, 10)) {
          // Token expired, try to refresh
          return await this.refreshToken(serverId);
        }
      }

      return true;

    } catch (error) {
      return false;
    }
  }

  /**
   * Get user info for a specific server
   */
  async getUserInfo(serverId) {
    try {
      const storageKeys = getStorageKeys(serverId);
      const userInfoStr = localStorage.getItem(storageKeys.USER_INFO);
      
      if (userInfoStr) {
        return JSON.parse(userInfoStr);
      }

      // Try to fetch fresh user info
      const userInfo = await this.fetchUserInfo(serverId);
      if (userInfo) {
        localStorage.setItem(storageKeys.USER_INFO, JSON.stringify(userInfo));
      }
      
      return userInfo;

    } catch (error) {
      return null;
    }
  }

  /**
   * Get server name for a specific server
   */
  getServerName(serverId) {
    const storageKeys = getStorageKeys(serverId);
    return localStorage.getItem(storageKeys.SERVER_NAME) || serverId;
  }

  /**
   * Refresh access token for a specific server
   */
  async refreshToken(serverId) {
    try {
      const storageKeys = getStorageKeys(serverId);
      const refreshToken = localStorage.getItem(storageKeys.REFRESH_TOKEN);
      
      if (!refreshToken) {
        return false;
      }

      const response = await getAuthorizedFetch()(`${config.bffUrl}/bff/auth/generic-mcp/${serverId}/refresh`, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        // Refresh failed, clear tokens
        this.logout(serverId);
        return false;
      }

      const tokens = await response.json();
      
      // Update stored tokens
      localStorage.setItem(storageKeys.ACCESS_TOKEN, tokens.access_token);
      
      if (tokens.refresh_token) {
        localStorage.setItem(storageKeys.REFRESH_TOKEN, tokens.refresh_token);
      }
      
      if (tokens.expires_in) {
        const expiresAt = Date.now() + (tokens.expires_in * 1000);
        localStorage.setItem(storageKeys.EXPIRES_AT, expiresAt.toString());
      }

      // Notify backend about updated credentials
      await this.notifyBackendCredentials(serverId, tokens.access_token, tokens.refresh_token);

      return true;

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Generic MCP (${serverId}) token refresh error:`, error);
      this.logout(serverId);
      return false;
    }
  }

  /**
   * Get access token for a specific server
   */
  async getAccessToken(serverId) {
    const isAuth = await this.isAuthenticated(serverId);
    if (!isAuth) {
      return null;
    }
    
    const storageKeys = getStorageKeys(serverId);
    return localStorage.getItem(storageKeys.ACCESS_TOKEN);
  }

  /**
   * Logout from a specific server
   */
  logout(serverId) {
    const storageKeys = getStorageKeys(serverId);
    localStorage.removeItem(storageKeys.ACCESS_TOKEN);
    localStorage.removeItem(storageKeys.REFRESH_TOKEN);
    localStorage.removeItem(storageKeys.EXPIRES_AT);
    localStorage.removeItem(storageKeys.USER_INFO);
    // Keep server name for display purposes
  }

  /**
   * Logout from all servers
   */
  logoutAll() {
    // Clear all generic MCP tokens
    Object.keys(localStorage).forEach(key => {
      // eslint-disable-next-line no-underscore-dangle
      if (key.startsWith('generic_mcp_')) {
        localStorage.removeItem(key);
      }
    });
  }
}

// Export singleton instance
const genericMcpAuth = new GenericMcpAuth();
export default genericMcpAuth;
